import { Monitor, Moon, Sun } from "lucide-react";
import { Button } from "./ui/button";
import { useTheme } from "./theme-provider";
import { useLanguage } from "../i18n/language-provider";

type Theme = "light" | "dark" | "system";

/** light → dark → system → light. "system" is a real stop, not a hidden default. */
const NEXT_THEME: Record<Theme, Theme> = {
  light: "dark",
  dark: "system",
  system: "light",
};

const ICON: Record<Theme, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

interface ThemeToggleProps {
  /**
   * The outline box is drawn for the paper pages and paints their background.
   * The rail draws its controls as bare icons in its own colours, so it asks
   * for `ghost` and passes those colours as `className`.
   */
  variant?: "outline" | "ghost";
  className?: string;
}

/**
 * Tri-state appearance control: light, dark, or match the device.
 *
 * The icon shows the state the app is IN, not the state a click would produce —
 * with three stops, an action-only icon can't say whether "sun" means "currently
 * light" or "click for light". The action goes in the tooltip instead.
 *
 * While on the system step the label also names the appearance the device
 * currently resolves to, so a device that reports something unexpected (a
 * browser with its own light/dark override, for instance) is visible rather
 * than mysterious.
 */
export function ThemeToggle({ variant = "outline", className }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { t } = useLanguage();

  const current = theme as Theme;
  const Icon = ICON[current];

  const action = {
    light: t.theme.switchToDark,
    dark: t.theme.switchToSystem,
    system: t.theme.pinLight,
  }[current];

  const modeLabel = resolvedTheme === "dark" ? t.theme.dark : t.theme.light;
  const label =
    current === "system"
      ? `${t.theme.matchingSystem.replace("{mode}", modeLabel)} ${action}`
      : action;

  return (
    <Button
      variant={variant}
      size="icon"
      onClick={() => setTheme(NEXT_THEME[current])}
      title={label}
      aria-label={label}
      className={className}
    >
      <Icon className="h-[1.2rem] w-[1.2rem]" />
    </Button>
  );
}
